import { useState, useEffect, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { avText, avBg, avFg } from "../data/constants";
import { fetchConsumersUsers } from "../services/usersService";

// ── SVG assets ────────────────────────────────────────────────────────────────
import SearchIcon from "../assets/user/Search.svg";
import DropdownIcon from "../assets/user/dropdown.svg";
import FilterIcon from "../assets/user/filter.svg";
import EyeIcon from "../assets/user/eye.svg";

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const s = (status || "").toLowerCase();
  if (s === "active" || s === "approved") {
    return (
      <span className="bg-[#0B61721A] text-[#0B6172] border border-[#0B617233] px-3.5 py-1 rounded-full text-xs font-semibold">
        Active
      </span>
    );
  }
  return (
    <span className="bg-[#5E5E5E1A] text-gray-500 border border-[#5E5E5E33] px-3.5 py-1 rounded-full text-xs font-semibold">
      Inactive
    </span>
  );
}

// ── Pagination ────────────────────────────────────────────────────────────────
function Pagination({ page, total, perPage, onChange }) {
  const totalPages = Math.ceil(total / perPage);
  if (totalPages <= 1) return null;

  const getPages = () => {
    if (totalPages <= 6) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const delta = 1;
    const range = [];
    for (let i = Math.max(2, page - delta); i <= Math.min(totalPages - 1, page + delta); i++) {
      range.push(i);
    }
    const result = [1];
    if (range[0] > 2) result.push("...");
    range.forEach((p) => result.push(p));
    if (range[range.length - 1] < totalPages - 1) result.push("...");
    result.push(totalPages);
    return result;
  };

  const pages = getPages();

  return (
    <div className="flex justify-between items-center px-[18px] py-[14px] border-t border-[#0000001A]">
      <p className="text-sm text-[#777]">
        Showing{" "}
        <strong className="text-[#333]">
          {Math.min((page - 1) * perPage + 1, total)}-{Math.min(page * perPage, total)}
        </strong>{" "}
        of <strong className="text-[#333]">{total}</strong> users
      </p>
      <div className="flex items-center gap-1.5">
        <button
          className="w-9 h-9 rounded-lg border border-[#0000001A] bg-white cursor-pointer flex items-center justify-center text-sm text-[#444] font-medium disabled:opacity-30"
          disabled={page === 1}
          onClick={() => onChange(Math.max(1, page - 1))}
        >
          ‹
        </button>
        {pages.map((p, idx) =>
          p === "..." ? (
            <span key={`ellipsis-${idx}`} className="w-9 text-center text-[#888] text-sm">
              …
            </span>
          ) : (
            <button
              key={p}
              className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm ${
                p === page
                  ? "bg-[#0B6172] text-white border-none font-bold"
                  : "border border-[#0000001A] bg-white text-[#444] font-medium cursor-pointer"
              }`}
              onClick={() => onChange(p)}
            >
              {p}
            </button>
          )
        )}
        <button
          className="w-9 h-9 rounded-lg border border-[#0000001A] bg-white cursor-pointer flex items-center justify-center text-sm text-[#444] font-medium disabled:opacity-30"
          disabled={page === totalPages}
          onClick={() => onChange(Math.min(totalPages, page + 1))}
        >
          ›
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const LIMIT = 10;

export default function ZytamaUsersPage() {
  const navigate = useNavigate();
  const [usersList, setUsersList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [pg, setPg] = useState(1);

  // ── Fetch users ─────────────────────────────────────────────────────────────
  const loadUsers = useCallback(async (page, query, status) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchConsumersUsers({ page, limit: LIMIT, q: query, status });
      setUsersList(data.users || []);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      setError(err.message || "Failed to load users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers(pg, search, selectedStatus);
  }, [pg, search, selectedStatus, loadUsers]);

  // ── Format date ──────────────────────────────────────────────────────────────
  const formatDate = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    if (isNaN(date.getTime())) return d;
    return date
      .toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
      .toUpperCase();
  };

  return (
    <div className="flex flex-col gap-4">
      {/* ── Header card ─────────────────────────────────────────────────────── */}
      <div className="bg-[#FFFFFFB2] border border-[#0000001A] rounded-[20px] shadow-[0_4px_32px_0_#0000000A] backdrop-blur-[20px] px-6 py-5 flex items-center justify-between">
        <div>
          <h1 className="font-[800] text-2xl text-gray-900 m-0">Zytama Users</h1>
          <p className="text-[13px] text-[#0B6172] mt-1">
            All registered users on the Zytama app — {total} member{total !== 1 ? "s" : ""} total.
          </p>
        </div>
      </div>

      {/* ── Filters + table card ─────────────────────────────────────────────── */}
      <div className="bg-[#FFFFFFB2] border border-[#0000001A] rounded-[20px] overflow-hidden">
        {/* ── Top bar ─────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 px-[18px] py-4 flex-wrap">
          {/* Search */}
          <div className="h-[42px] bg-white border border-[#0000001A] rounded-lg flex items-center gap-2 px-3.5 flex-1 min-w-[220px]">
            <img src={SearchIcon} alt="search" className="w-4 h-4 shrink-0" />
            <input
              type="text"
              placeholder="Search by name, email or phone..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPg(1); }}
              className="flex-1 border-none outline-none bg-transparent text-sm text-gray-600"
            />
          </div>

          {/* Status filter */}
          {/* <div className="h-[42px] bg-white border border-[#0000001A] rounded-lg flex items-center gap-2 px-3.5 relative min-w-[130px]">
            <select
              value={selectedStatus}
              onChange={(e) => { setSelectedStatus(e.target.value); setPg(1); }}
              className="w-full bg-transparent border-none outline-none appearance-none pr-6 text-sm text-[#444] font-medium cursor-pointer"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <img src={DropdownIcon} alt="dropdown" className="w-3 h-2 absolute right-3 pointer-events-none" />
          </div> */}

          {/* Filter icon */}
          {/* <div className="h-[42px] bg-white border border-[#0000001A] rounded-lg flex items-center gap-2 px-3 cursor-pointer w-[42px] justify-center">
            <img src={FilterIcon} alt="filter" className="w-[18px] h-3" />
          </div> */}
        </div>

        {/* Horizontal scroll container for the table */}
        <div className="overflow-x-auto w-full [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="min-w-[850px]">
            {/* ── Table header ────────────────────────────────────────────────── */}
            <div className="grid grid-cols-[4fr_4fr_3fr_1fr] bg-[#5E5E5E14] border border-b-0 border-[#0000001A] rounded-t-lg px-[18px] py-[13px] mx-[18px]">
              {["USER", "EMAIL", "JOINED", "ACTIONS"].map((col, i) => (
                <div
                  key={col}
                  className={`text-xs font-bold text-gray-500 tracking-wider uppercase ${
                    i === 3 ? "text-right" : "text-left"
                  }`}
                >
                  {col}
                </div>
              ))}
            </div>

            {/* ── Table rows ──────────────────────────────────────────────────── */}
            <div className="px-[18px] pt-2.5 pb-1.5 flex flex-col gap-2.5 overflow-y-auto max-h-[calc(100vh-450px)] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-10 gap-3">
                  <div className="w-7 h-7 border-2 border-[#DBF5F0] border-t-[#0B6172] rounded-full animate-spin" />
                  <p className="text-[13px] text-[#0B6172] font-semibold">Loading users...</p>
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-14 gap-3 text-gray-400">
                  <p className="font-bold text-red-500 text-[15px]">Failed to load users</p>
                  <p className="text-[13px]">{error}</p>
                  <button
                    onClick={() => loadUsers(pg)}
                    className="mt-2 px-4 py-2 bg-[#0B6172] text-white rounded-lg text-sm font-semibold hover:bg-[#094f5e] transition-colors"
                  >
                    Retry
                  </button>
                </div>
              ) : usersList.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-14 gap-3 text-gray-400">
                  <p className="font-bold text-gray-500 text-[15px]">No users found</p>
                  <p className="text-[13px]">Try adjusting your search or filters</p>
                </div>
              ) : (
                usersList.map((u) => (
                  <div
                    key={u.id}
                    className="grid grid-cols-[4fr_4fr_3fr_1fr] items-center bg-white border border-[#0000001A] rounded-lg px-[18px] py-3.5"
                  >
                    {/* User: avatar + name */}
                    <div className="flex items-center gap-3.5">
                      <div
                        className="w-[38px] h-[38px] rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                        style={{
                          background: avBg(u.name),
                          color: avFg(u.name),
                          border: `1.5px solid ${avFg(u.name)}40`,
                        }}
                      >
                        {avText(u.name)}
                      </div>
                      <div>
                        <p className="text-[14px] font-semibold text-gray-900 m-0 leading-tight">
                          {u.name}
                        </p>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="text-[13px] text-gray-500 font-medium truncate pr-2">
                      {u.email}
                    </div>

                    {/* Joined */}
                    <div className="text-[13px] text-gray-500 font-medium uppercase">
                      {formatDate(u.joined_date)}
                    </div>

                    {/* Actions */}
                    <div className="text-right">
                      <button
                        onClick={() => navigate(`/zytama-users/${u.id}`)}
                        className="w-9 h-9 rounded-lg border-none bg-transparent cursor-pointer flex items-center justify-center hover:bg-gray-100 transition-colors ml-auto"
                        title="View details"
                      >
                        <img src={EyeIcon} alt="view" className="w-[18px] h-[18px]" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* ── Pagination ──────────────────────────────────────────────────── */}
        {total > LIMIT && (
          <Pagination page={pg} total={total} perPage={LIMIT} onChange={setPg} />
        )}
      </div>
    </div>
  );
}
